import React, { useState, useEffect } from "react"
import { graphql, Link } from "gatsby"
import { GatsbyImage } from "gatsby-plugin-image"
import styled from "styled-components"
import { media } from "src/styles/breakpoints"
import useWindowSize from "src/hooks/useWindowSize"
import { animateScroll } from "react-scroll"
import Seo from "src/components/SEO"
import {
  PrimaryTitle,
  SecondaryTitle,
  Statement,
} from "src/components/Typography"
import BaseContainer from "src/components/Base/BaseContainer"
import BaseIcon from "src/components/Base/BaseIcon"
import ResourcesSearchBar from "src/components/ResourcesSearchBar"
import ResourceTile from "src/components/ResourceTile"
import SectionTextWithCircle from "src/components/Section/SectionTextWithCircle"
import staticCategories from "../../../data/resources"
import { slugify } from "src/utils/helpers"
import { get } from "lodash"
import shadow from "src/assets/images/box-shadow.png"
import ContactPersonWrapper from "src/components/ContactPersonWrapper"
import UnderConstruction from "src/components/UnderConstruction"

export const pageQuery = graphql`
  {
    allStrapiCategory {
      nodes {
        name
        description
        strapiId
        parent_category {
          name
          id
        }
        resources {
          id
        }
        child_categories {
          name
        }
      }
    }
    allStrapiResource(sort: { fields: createdAt, order: DESC }) {
      nodes {
        Title
        category {
          id
          parent_category
        }
        featured
        createdAt(formatString: "MMMM D, YYYY")
        content_type
        number_of_files
        Preview {
          url
          localFile {
            childImageSharp {
              gatsbyImageData(height: 143, quality: 80, layout: FIXED)
            }
          }
        }
      }
    }
    heroBg: file(relativePath: { eq: "resources-hero-bg.jpg" }) {
      childImageSharp {
        gatsbyImageData(quality: 80, layout: FULL_WIDTH)
      }
    }
  }
`

const ResourcesPage = ({ data }) => {
  // Show under construction message
  // This will display for both direct navigation and client-side routing
  return (
    <>
      <Seo title="Resources - Under Construction" />
      <UnderConstruction />
    </>
  )

  // Original page content (temporarily commented out)
  /*
  const [featuredTab, setFeaturedTab] = useState(0)

  // Check if we should show under construction message
  // This catches both direct navigation and client-side routing
  const showUnderConstruction = typeof window !== 'undefined' && window.location.pathname === '/resources'

  const allCategories = data.allStrapiCategory.nodes
  const resources = data.allStrapiResource.nodes
  const featuredResources = resources.filter((r) => r.featured)
  const rootPath = "/resources"

  const featuredCategories = [
    {
      name: "Projects for adaptation",
      id: "5eba80ee73f8be00173e40dc",
    },
    {
      name: "Articles",
      id: "5eba810573f8be00173e40de",
    },
    {
      name: "Talks and lectures",
      id: "5ecfdb61f789cd0017f92938",
    },
  ]

  function onHeroButtonClick() {
    const scrollTop = Math.min(window.innerHeight, window.innerWidth * 0.7)
    animateScroll.scrollTo(scrollTop, {
      duration: 900,
      smooth: "easeInOutQuart",
    })
  }

  function getResourcePath(categoryId, title) {
    if (!categoryId) {
      return rootPath
    }

    const categories = []

    function getCat(id) {
      allCategories.forEach((category) => {
        const isCat = category.strapiId === id
        if (!isCat) {
          return
        }
        categories.push(slugify(category.name))
        if (category.parent_category) {
          getCat(category.parent_category.id)
        }
      })
    }

    getCat(categoryId)

    const path = `${rootPath}/${categories
      .reverse()
      .slice(0, 1)
      .join("/")}/${slugify(title)}`

    return path
  }

  function renderResourceList(resourceList, showDateAdded = false) {
    return (
      <ResourceList>
        {resourceList.map(
          (
            {
              Title,
              category,
              content_type,
              number_of_files,
              createdAt,
              Preview,
            },
            index
          ) => (
            <ResourceTile
              path={getResourcePath(category && category.id, Title)}
              title={Title}
              type={content_type}
              numberOfFiles={number_of_files}
              showDateAdded={showDateAdded}
              dateAdded={createdAt}
              imageSharp={get(Preview[0], "localFile.childImageSharp")}
              imageSharpSequence={
                Preview.length > 0 &&
                Preview.slice(0, 10).map((p) =>
                  get(p, "localFile.childImageSharp")
                )
              }
              key={index}
            />
          )
        )}
      </ResourceList>
    )
  }

  // Show under construction message if needed
  if (showUnderConstruction) {
    return (
      <UnderConstructionWrapper>
        <Seo title="Resources - Under Construction" />
        <BaseContainer>
          <UnderConstructionContent>
            <UnderConstructionTitle>Page Under Construction</UnderConstructionTitle>
            <UnderConstructionMessage>
              Our resources page is temporarily unavailable while we make improvements. 
              Please check back later.
            </UnderConstructionMessage>
            <BackLink to="/">← Return to Homepage</BackLink>
          </UnderConstructionContent>
        </BaseContainer>
      </UnderConstructionWrapper>
    )
  }

  return (
    <Wrapper>
      <Seo title="Resource library" />
      <Hero windowHeight={useWindowSize().height}>
        <HeroBackground
          image={data.heroBg.childImageSharp.gatsbyImageData}
          alt=""
          style={{ position: "absolute" }}
        />
        <BaseContainer>
          <HeroContent>
            <HeroText as="h1">
              Learn how to start an organisation or kickstart your campaign.
              Download our resources for free.
            </HeroText>
            <HeroButton onClick={onHeroButtonClick}>
              Explore resources <Icon name="arrow-down" source="feather" />
            </HeroButton>
          </HeroContent>
          <ResourcesSearchBar />
        </BaseContainer>
      </Hero>
      <SectionCategories id="categories">
        <BaseContainer>
          <PrimaryTitle>Browse by category</PrimaryTitle>
          <CategoryList>
            {staticCategories.map(({ name, slug, icon, color }, index) => {
              const description = allCategories.find(
                (c) => c.name === name
              ).description
              return (
                <Category to={`${rootPath}/${slug}`} key={index}>
                  <CategoryHelper />
                  <CategoryIconWrapper style={{ color }}>
                    <CategoryIcon name={icon} source="feather" />
                  </CategoryIconWrapper>
                  <CategoryName>{name}</CategoryName>
                  <CategoryDescription>{description}</CategoryDescription>
                </Category>
              )
            })}
          </CategoryList>
        </BaseContainer>
      </SectionCategories>
      <SectionFeatured>
        <BaseContainer>
          <FeaturedTabs>
            {["Featured", "New"].map((name, index) => (
              <FeaturedTab
                className={index === featuredTab ? "-active" : ""}
                key={index}
                onClick={() => {
                  setFeaturedTab(index)
                }}
              >
                {name}
              </FeaturedTab>
            ))}
          </FeaturedTabs>
          <FeaturedPanes>
            {featuredTab === 0 && (
              <FeaturedPane>
                {featuredCategories.map(({ name, id }, index) => (
                  <FeaturedRow key={index}>
                    <FeaturedRowHeader>
                      <FeaturedRowTitle>{name}</FeaturedRowTitle>
                      <FeaturedRowMore to={`${rootPath}/${slugify(name)}`}>
                        See all
                        <FeaturedRowMoreIcon name="arrow-right" />
                      </FeaturedRowMore>
                    </FeaturedRowHeader>
                    {renderResourceList(
                      featuredResources
                        .filter(
                          (r) =>
                            (r.category && r.category.id === id) ||
                            (r.parent_category && r.parent_category.id === id)
                        )
                        .slice(0, 4)
                    )}
                  </FeaturedRow>
                ))}
              </FeaturedPane>
            )}
            {featuredTab === 1 && (
              <FeaturedPane>
                {renderResourceList(resources.slice(0, 12), true)}
              </FeaturedPane>
            )}
          </FeaturedPanes>
        </BaseContainer>
      </SectionFeatured>
      <SectionTextWithCircle
        title="Our mission"
        text="Anima International was designed as a hub of knowledge and catalyst for change. We aim to empower people around the world who want to help animals by providing them with the information, resources, advice and inspiration they need to change the world for the better."
        image={{
          url: "/images/resources/mission.jpg",
          url2x: "/images/resources/mission@2x.jpg",
        }}
      />
      <ContactPersonWrapper
        title="Contact us"
        subtitle="If you have any questions regarding the Resource Library, please reach out! We are here to give you additional guidance, provide more materials, or even create videos on your request."
        image="/images/team/alevtyna.jpg"
        name="Alevtyna Kozachok"
        position="Resource Library Manager"
        email="alevtyna.kozachok@animainternational.org"
      />
    </Wrapper>
  )
  */
}

const Wrapper = styled.div`
  margin-bottom: 130px;

  ${media.tablet} {
    margin-bottom: 0;
  }
`

const Icon = styled(BaseIcon)``

const Hero = styled.div`
  height: 100vh;
  max-height: ${(p) => p.windowHeight}px;
  margin-bottom: 30px;
  position: relative;
  color: #fff;

  ${BaseContainer} {
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    align-items: flex-start;
    height: 100%;
    padding-bottom: 90px;
    position: relative;
  }

  ${media.tablet} {
    margin-bottom: 90px;

    ${BaseContainer} {
      padding-bottom: 100px;
    }
  }

  ${media.desktop} {
    max-height: 70vw;
    margin-bottom: 135px;
  }
`

const HeroBackground = styled(GatsbyImage)`
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: none;
`

const HeroContent = styled.div``

const HeroText = styled(Statement)`
  max-width: 510px;
  margin-bottom: 0;
  color: inherit;
`

const HeroButton = styled.button`
  margin-top: 30px;
  margin-left: 4px;
  height: 50px;
  text-transform: uppercase;
  font-weight: 600;
  font-size: 12px;
  line-height: 50px;
  letter-spacing: 0.1em;

  ${Icon} {
    width: 16px;
    height: 16px;
    margin-top: -1px;
    margin-left: 10px;
    transition: transform 0.25s;
  }

  &:hover {
    ${Icon} {
      transform: translateY(5px);
    }
  }
`

const SectionCategories = styled.section`
  overflow: hidden;
  padding-top: 70px;
  padding-bottom: 120px;

  ${media.tablet} {
    padding-bottom: 140px;
  }

  ${media.desktop} {
    padding-bottom: 180px;
  }
`

const CategoryList = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-left: -20px;
  margin-right: -20px;

  ${media.tablet} {
    margin-left: -30px;
    margin-right: -30px;
  }
`

const Category = styled(Link)`
  display: block;
  width: calc(100% / 2);
  padding: 20px 20px 25px;
  position: relative;

  ${media.tablet} {
    width: calc(100% / 3);
    padding: 40px 30px 45px;
  }

  ${media.desktop} {
    width: 305px;

    &:not(:nth-child(3n + 1)) {
      margin-left: calc((100% - 305px * 3) / 2);
    }
  }
`

const CategoryHelper = styled.div`
  ${media.pointer} {
    opacity: 0;
    z-index: -1;
    pointer-events: none;
    transition: opacity 0.15s;

    &,
    &::after {
      width: 100%;
      height: 100%;
      position: absolute;
      top: 0;
      left: 0;
    }

    &::before {
      content: "";
      width: 411px;
      height: 395px;
      position: absolute;
      top: -10px;
      left: 50%;
      transform: translateX(-50%);
      background: url(${shadow}) no-repeat;
    }

    &::after {
      content: "";
      background: #fff;
      border-radius: 15px;
    }

    ${Category}:hover & {
      opacity: 1;
    }
  }
`

/* eslint-disable limited-exports-page-templates */
export const CategoryIconWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 60px;
  height: 60px;
  margin-bottom: 25px;
  position: relative;

  &::before {
    content: "";
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    border-radius: 15px;
    box-shadow: 0 20px 40px rgba(0, 11, 33, 0.06);
    transition: 0.15s;
  }

  ${Category}:hover & {
    &::before {
      /* transform: scale(1.2); */
      box-shadow: none;
    }
  }
`

export const CategoryIcon = styled(BaseIcon)``

const CategoryName = styled(SecondaryTitle)`
  margin-bottom: 12px;
`

const CategoryDescription = styled.p`
  font-size: 15px;
  line-height: 1.6;
  color: ${(p) => p.theme.color.textLight};
`

const SectionFeatured = styled.section`
  margin-bottom: 80px;

  ${media.tablet} {
    margin-bottom: 120px;
  }

  ${media.desktop} {
    margin-bottom: 180px;
  }
`

const FeaturedTabs = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 60px;
`

const FeaturedTab = styled.div`
  height: 50px;
  margin: 0 5px 10px;
  padding: 0 20px;
  position: relative;
  border: 1px solid transparent;
  border-radius: 25px;
  line-height: 50px;
  white-space: nowrap;
  color: ${(p) => p.theme.color.textLight};
  cursor: pointer;
  user-select: none;
  /* outline: 1px solid red; */
  transition: color 0.15s;

  &:hover {
    border-color: #cfdbdb;
  }

  &.-active {
    border-color: ${(p) => p.theme.color.primary};
    color: ${(p) => p.theme.color.text};
  }
`

const FeaturedRow = styled.div`
  &:not(:last-child) {
    margin-bottom: 85px;
  }
`

const FeaturedRowHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 30px;
`

const FeaturedRowTitle = styled(SecondaryTitle)`
  margin-bottom: 0;
`

const FeaturedRowMore = styled(Link)`
  display: inline-block;
  position: relative;
  color: #076262;
  transition: color 0.25s;

  &::before {
    content: "";
    width: 0%;
    height: 2px;
    position: absolute;
    left: 0;
    bottom: -5px;
    background: ${(p) => p.theme.color.primary};
    pointer-events: none;
    transition: width 0.25s;
  }

  &:hover {
    &::before {
      width: 20px;
    }
  }
`

const FeaturedRowMoreIcon = styled(BaseIcon)`
  width: 13px;
  height: 13px;
  margin-top: -1px;
  margin-left: 17px;
  transition: 0.25s;

  ${FeaturedRowMore}:hover & {
    transform: translateX(3px);
  }
`

const ResourceList = styled.div`
  display: flex;
  flex-wrap: wrap;

  &:first-child {
    padding-top: 10px;
  }

  > * {
    width: calc((100% - 20px) / 2);

    &:not(:nth-child(2n + 1)) {
      margin-left: 20px;
    }

    &:nth-child(n + 3) {
      margin-top: 30px;
    }
  }

  ${media.tablet} {
    > * {
      width: calc((100% - 30px * 2) / 3);

      &:not(:nth-child(2n + 1)) {
        margin-left: 0;
      }

      &:not(:nth-child(3n + 1)) {
        margin-left: 30px;
      }

      &:nth-child(n + 3) {
        margin-top: 0;
      }

      &:nth-child(n + 4) {
        margin-top: 30px;
      }
    }
  }

  ${media.desktop} {
    > * {
      width: calc((100% - 30px * 3) / 4);

      &:not(:nth-child(3n + 1)) {
        margin-left: 0;
      }

      &:not(:nth-child(4n + 1)) {
        margin-left: 30px;
      }

      &:nth-child(n + 4) {
        margin-top: 0;
      }

      &:nth-child(n + 5) {
        margin-top: 30px;
      }

      ${FeaturedRow} & {
        &:nth-child(n + 4) {
          display: block;
        }
      }
    }
  }
`

const FeaturedPanes = styled.div``

const FeaturedPane = styled.div``

export default ResourcesPage
